import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AlertController, IonicModule, ToastController } from '@ionic/angular';
import { BerService } from '../ber.service';
import { BER_OPCIOK } from '../ber.opciok';
import { AdatszolgApi, evLista, ftSzoveg, HONAPNEV } from './adatszolg.api';
import { adatTabla, datum, dokumentumNyomtat, esc, ft } from './dokumentum-nyomtatas';

/*  BÉR – Bérfeladás (RLB: Bér adatszolgáltatás → Bérfeladás).
 *
 *  Előbb MEGMUTATJA, mit könyvelne a feladás a kapcsolt NAPLO-ügyfél naplófőkönyvébe (tétel-fej,
 *  rovatok, összegek, új rovatok, korábbi feladás), és csak utána ad fel. Az előnézet a
 *  ber-adatszolg.js `/berfeladas-elonezet` végpontja (írás nélkül, a ber.js feladásának tükre); a
 *  feladás maga a meglévő ber.js `POST /berfeladas/:ugyfelId`.
 */
@Component({
  selector: 'app-ber-berfeladas',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule],
  templateUrl: './berfeladas.component.html',
  styleUrls: ['../../naplo/naplo-shared.scss', '../ber-shared.scss', './adatszolg.component.scss'],
})
export class BerfeladasComponent implements OnInit {
  evek = evLista();
  honapok = BER_OPCIOK.honapok;
  adat: any = null;
  tolt = false;
  kuld = false;
  /** Kettős: a főkönyvi számok szerkesztője (üres = alapértelmezés). */
  fk: Record<string, string> = {};
  fkMent = false;
  get fkKulcsok(): string[] { return Object.keys(this.adat?.fokonyv_alap || {}); }

  constructor(private ber: BerService, private api: AdatszolgApi, private alert: AlertController, private toast: ToastController) {}

  get aktiv() { return this.ber.aktiv; }
  get idoszakNev(): string { return `${this.aktiv.ev}. ${HONAPNEV[this.aktiv.ho - 1] || ''}`; }

  async ngOnInit() { await this.ber.init(); await this.betolt(); }
  async evValt(ev: number) { await this.ber.setAktiv({ ev }); await this.betolt(); }
  async hoValt(ho: number) { await this.ber.setAktiv({ ho }); await this.betolt(); }

  async betolt() {
    if (!this.aktiv.ugyfelId) return;
    this.tolt = true;
    try {
      this.adat = await this.api.get(`/berfeladas-elonezet/${this.aktiv.ugyfelId}?ev=${this.aktiv.ev}&ho=${this.aktiv.ho}`);
      const fk = this.adat?.fokonyv || {}, alap = this.adat?.fokonyv_alap || {};
      this.fk = Object.fromEntries(Object.keys(alap).map((k) => [k, fk[k] && fk[k] !== alap[k] ? fk[k] : '']));
    }
    catch (e: any) { this.adat = null; this.hiba(this.api.hibaUzenet(e, 'Nem sikerült betölteni az előnézetet.')); }
    finally { this.tolt = false; }
  }

  get parokOsszesen(): number { return (this.adat?.parok || []).reduce((x: number, p: any) => x + Number(p.osszeg || 0), 0); }
  get sorokOsszesen(): number { return (this.adat?.sorok || []).reduce((x: number, s: any) => x + Number(s.osszeg || 0), 0); }

  async felad() {
    if (!this.adat?.feladhato || this.kuld) return;
    const ismetelt = (this.adat.korabbi || []).length > 0;
    const a = await this.alert.create({
      header: ismetelt ? 'Ismételt bérfeladás' : 'Bérfeladás a NAPLO-ba',
      message: `${this.idoszakNev}: ${this.adat.sorok.length} tételsor, összesen ${ftSzoveg(this.sorokOsszesen)} Ft a(z) `
        + `${this.adat.naplo?.nev || 'kapcsolt'} naplófőkönyvébe (${this.adat.tetel?.datum}, ${this.adat.tetel?.sorszam}. tétel).`
        + (ismetelt ? ' EZ A HÓNAP MÁR FEL LETT ADVA – a régi feladás törlődik, és az új kerül a helyére.' : ''),
      buttons: [{ text: 'Mégse', role: 'cancel' }, { text: ismetelt ? 'Felülírás' : 'Feladás', role: 'confirm' }],
    });
    await a.present();
    const { role } = await a.onDidDismiss();
    if (role !== 'confirm') return;
    this.kuld = true;
    try {
      const r: any = await this.ber.post('/berfeladas/' + this.aktiv.ugyfelId + this.ber.evhoq(), { felulir: ismetelt });
      this.ok(`Feladva: bér ${ftSzoveg(r.brutto)} Ft, levonások ${ftSzoveg(r.levonasok)} Ft, ${r.kiva ? 'KIVA' : 'szocho'} ${ftSzoveg(r.munkaltatoi)} Ft.`);
      await this.betolt();
    } catch (e: any) { this.hiba(e?.error?.error || 'A bérfeladás nem sikerült.'); }
    finally { this.kuld = false; }
  }

  async fokonyvMent() {
    if (this.fkMent) return;
    this.fkMent = true;
    try {
      await this.api.put(`/berfeladas-fokonyv/${this.aktiv.ugyfelId}`, this.fk);
      this.ok('A főkönyvi számok elmentve.');
      await this.betolt();
    } catch (e: any) { this.hiba(this.api.hibaUzenet(e, 'A mentés nem sikerült.')); }
    finally { this.fkMent = false; }
  }

  /** A feladás nyomtatása: a könyvelési tételek (kettősnél T/K főkönyvi számokkal), a dolgozónkénti bontás és a közterhek. */
  nyomtat() {
    const a = this.adat;
    if (!a?.tetel) return;
    const o = a.osszesen;
    const fej = `<div class="fej"><div><h1>Bérfeladás – ${esc(this.idoszakNev)}</h1>
        <div class="alcim">${esc(a.ugyfel?.nev || this.aktiv.ugyfelNev || '')} → ${esc(a.naplo?.nev || 'NAPLO')} · ${a.kettos ? 'kettős könyvvitel, vegyes napló' : 'egyszeres könyvvitel, naplófőkönyv'}</div></div>
      <div class="jobb">Bizonylat: <b>${esc(a.tetel.bizonylatszam)}</b><br>Kelte: ${esc(datum(a.tetel.datum))}<br>${esc(a.tetel.sorszam)}. tétel</div></div>`;
    const tetel = a.kettos
      ? adatTabla(['#', 'Tartozik', 'Követel', 'Megnevezés', 'Összeg (Ft)'],
        (a.parok || []).map((p: any, i: number) => [String(i + 1), `${p.tartozik.szamlaszam} ${p.tartozik.nev || ''}`, `${p.kovetel.szamlaszam} ${p.kovetel.nev || ''}`, p.megnevezes, ft(p.osszeg)]),
        [4], ['', '', '', 'Összesen', ft(this.parokOsszesen)])
      : adatTabla(['#', 'Rovatszám', 'Rovat', 'Tétel', 'Oszlop', 'Összeg (Ft)'],
        (a.sorok || []).map((s: any) => [String(s.sorszam), s.rovatszam, s.rovat_nev, s.cim, s.oszlop + '.', ft(s.osszeg)]),
        [4, 5], ['', '', '', 'Összesen', '', ft(this.sorokOsszesen)]);
    const kiva = o.kiva ? 'KIVA' : 'Szocho';
    const reszl = adatTabla(['Dolgozó', 'Bruttó', 'SZJA', 'TB-járulék', 'EKHO', kiva, 'Nettó'],
      (a.reszletek || []).map((r: any) => [r.alkalmazott_nev, ft(r.brutto), ft(r.szja), ft(r.tb_jarulek), ft(r.ekho), ft(r.szocho), ft(r.netto)]),
      [1, 2, 3, 4, 5, 6], ['Összesen', ft(o.brutto), ft(o.szja), ft(o.tb), ft(o.ekho), ft(o.munkaltatoi), ft(o.netto)]);
    const kt = a.kozterhek?.tetelek?.length ? `<h2>Fizetendő közterhek és utalások</h2>` + adatTabla(['Mit', 'Kinek', 'Számlaszám', 'Esedékes', 'Összeg (Ft)'],
      a.kozterhek.tetelek.map((t: any) => [t.megnevezes, t.kedvezmenyezett || '', t.szamla ? `${t.szamla}${t.adonem ? ' (adónem ' + t.adonem + ')' : ''}` : (t.megjegyzes || ''), t.esedekes ? datum(t.esedekes) : '', ft(t.osszeg)]),
      [4], ['A NAV-nak összesen', '', '', '', ft(a.kozterhek.nav_osszesen)]) : '';
    const lap = fej + `<h2>Könyvelési tétel</h2>` + tetel
      + `<div class="megj">${esc(a.kettos ? `Megnevezés: „${a.tetel.megjegyzes}”. A nettó bér (${ft(a.netto_marad)} Ft) a ${a.fokonyv?.elszamolas} számlán marad, a kifizetést a bankkivonat rendezi.` : `Megjegyzés a naplóban: „${a.tetel.megjegyzes}”.`)}</div>`
      + `<h2>Dolgozónként</h2>` + reszl + kt
      + `<div class="alairas"><div>Kelt: ${esc(new Date().toLocaleDateString('hu-HU'))}</div><div class="vonal"><span>könyvelő</span></div></div>`;
    dokumentumNyomtat(`Bérfeladás ${this.idoszakNev}`, [lap], 'Cronomit · Bérfeladás');
  }

  private async ok(m: string) { (await this.toast.create({ message: m, duration: 3000, color: 'success' })).present(); }
  private async hiba(m: string) { (await this.toast.create({ message: m, duration: 3500, color: 'danger' })).present(); }
}
